/**
 * When does a page need the real Clerk runtime? (M6 performance)
 *
 * Pure and shared by the server (decides during SSR from the request's cookie
 * header) and the browser (`src/client.tsx` preloads the Clerk chunk before
 * hydration when the server said so). The server's decision is written to
 * `<html data-clerk="1">`, and the client reads THAT attribute instead of
 * re-deciding, so SSR markup and hydration can never disagree.
 *
 * Clerk mounts when:
 *   1. the page renders Clerk's own UI (sign in, sign up, onboarding, profile),
 *      in any locale, including Clerk's path-routed sub-steps
 *      (`/login/factor-one`, `/register/verify-email-address`, ...); or
 *   2. the browser carries a Clerk session hint (`__client_uat` > 0), i.e. the
 *      visitor is (or very recently was) signed in.
 *
 * Everyone else is an anonymous visitor for whom Clerk could only ever report
 * "signed out": they get the instant anonymous auth state and never download
 * Clerk.
 */
import { LOCALES } from '../i18n/config'
import { localizedPath } from '../i18n/routes'
import type { RouteKey } from '../i18n/routes'
import { hasClerkSessionHint } from '../paywall'

/** `<html data-clerk="1">`: this document mounted the Clerk runtime. */
export const CLERK_MOUNT_ATTRIBUTE = 'data-clerk'

const CLERK_UI_ROUTES: ReadonlyArray<RouteKey> = [
  'login',
  'register',
  'onboarding',
  'settingsProfile',
]

const CLERK_UI_PATHS: ReadonlyArray<string> = CLERK_UI_ROUTES.flatMap((key) =>
  LOCALES.map((locale) => localizedPath(key, locale)),
)

/** True for pages that render Clerk UI (or one of its path-routed steps). */
export function isClerkUiPath(pathname: string): boolean {
  const clean = pathname.split(/[?#]/u)[0].replace(/\/+$/u, '') || '/'
  return CLERK_UI_PATHS.some(
    (path) => clean === path || clean.startsWith(`${path}/`),
  )
}

export function shouldMountClerk(pathname: string, cookieHeader: string): boolean {
  return isClerkUiPath(pathname) || hasClerkSessionHint(cookieHeader)
}
